// @ts-nocheck
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ownerApi } from './ownerApi';
import { useLoad } from './lib/useLoad';
import { businessDisplayName, formatNumber, scheduledPlanChangeSentence } from './lib/format';
import { label } from './lib/labels';
import { statusLabel, statusTone } from './lib/status';
import OwnerPeople from './OwnerPeople';
import PlanAssignment from './PlanAssignment';
import PageHeader from './ui/PageHeader';
import DataTable from './ui/DataTable';
import Badge from './ui/Badge';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import Drawer from './ui/Drawer';
import OverflowMenu from './ui/OverflowMenu';
import Toggle from './ui/Toggle';
import Alert from './ui/Alert';
import EmptyState from './ui/EmptyState';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';

export default function OwnerTenants() {
  const [params, setParams] = useSearchParams();
  const [sort, setSort] = useState('name');
  const [hideTest, setHideTest] = useState(true);
  const [pending, setPending] = useState(/** @type {any} */ (null));
  const [notice, setNotice] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const state = useLoad(() => ownerApi.subscribers().then((data) => data.subscribers || []), [reloadKey]);
  const openId = params.get('open') || '';
  const visible = useMemo(() => {
    const filtered = (state.data || []).filter((row) => !(hideTest && row.hide_by_default));
    filtered.sort((left, right) => {
      if (sort === 'messages') return Number(right.messages_remaining || 0) - Number(left.messages_remaining || 0);
      const a = sort === 'tenant' ? left.tenant_id : (left.business_name || left.email || '');
      const b = sort === 'tenant' ? right.tenant_id : (right.business_name || right.email || '');
      return String(a).localeCompare(String(b));
    });
    return filtered;
  }, [state.data, sort, hideTest]);
  const selected = (state.data || []).find((row) => row.tenant_id === openId);

  const exportCsv = () => {
    const header = 'tenant_id,business_name,email,membership,messages_remaining';
    const lines = visible.map((row) => [row.tenant_id, row.business_name, row.email, row.membership, row.messages_remaining]
      .map((value) => `"${String(value ?? '').replaceAll('"', '""')}"`).join(','));
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'tenants.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const cancelDowngrade = async (row) => {
    await ownerApi.cancelScheduledDowngrade({ tenant_id: row.tenant_id });
    setNotice(`Scheduled downgrade cancelled. ${businessDisplayName(row)} stays on ${label('plan', row.subscription || 'none')}.`);
    setReloadKey((current) => current + 1);
  };

  const hide = async (row) => {
    const owner = (row.users || []).find((user) => user.role === 'owner') || row.users?.[0];
    if (!owner?.id) return;
    await ownerApi.updateUser(owner.id, { status: 'blocked' });
    setNotice(`${businessDisplayName(row)} is hidden.`);
    setPending(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Businesses" subtitle="Every business using Linas AI. Click one to see details." actions={<Button variant="secondary" onClick={exportCsv}>Export CSV</Button>} />
      <PlanAssignment
        businesses={state.data || []}
        onGranted={(message) => {
          setNotice(message);
          setReloadKey((current) => current + 1);
        }}
      />
      {notice ? <p className="text-sm text-green-800">{notice}</p> : null}
      {state.status === 'error' ? <Alert title="We couldn't load businesses." detail={state.error} /> : null}
      <DataTable
        loading={state.status === 'loading'}
        columns={[
          { key: 'name', label: 'Business', render: (row) => <span className="font-semibold">{businessDisplayName(row)}</span> },
          {
            key: 'plan',
            label: 'Plan',
            render: (row) => (
              <span className="text-sm">
                {label('plan', row.subscription || 'none')} · {label('membership', row.membership || 'none')}
                {row.pending_downgrade ? (
                  <span className="mt-1 block text-slate-600">
                    {scheduledPlanChangeSentence(label('plan', row.pending_downgrade.plan_id), row.pending_downgrade.effective_at)}
                  </span>
                ) : null}
              </span>
            ),
          },
          { key: 'messages', label: 'Messages left', align: 'right', render: (row) => formatNumber(row.messages_remaining) },
          { key: 'status', label: 'Status', render: (row) => <Badge tone={statusTone('user', row.hide_by_default ? 'test' : 'active')}>{statusLabel('user', row.hide_by_default ? 'test' : 'active')}</Badge> },
          {
            key: 'more',
            label: '',
            render: (row) => (
              <OverflowMenu
                items={[
                  { id: 'open', label: 'Open', onClick: () => setParams({ open: row.tenant_id }) },
                  { id: 'hide', label: 'Hide business', danger: true, onClick: () => setPending(row) },
                ]}
              />
            ),
          },
        ]}
        rows={visible}
        getRowId={(row) => row.tenant_id}
        searchPlaceholder="Search by business, email or ID"
        toolbar={(
          <>
            <select aria-label="Sort" value={sort} onChange={(event) => setSort(event.target.value)} className="h-9 rounded-lg border border-[#7C8798] px-3 text-sm">
              <option value="name">Name A–Z</option>
              <option value="messages">Most messages left</option>
            </select>
            <Toggle checked={!hideTest} onChange={(next) => setHideTest(!next)} label="Show test accounts" />
          </>
        )}
        searchText={(row) => `${row.business_name} ${row.email} ${row.tenant_id}`}
        onRowClick={(row) => setParams({ open: row.tenant_id })}
        empty={state.status === 'ready' ? <EmptyState icon={<BuildingStorefrontIcon className="h-6 w-6" />} title="No businesses yet" text="Businesses appear here when they sign up in the app." /> : null}
      />
      {selected ? (
        <Drawer title={businessDisplayName(selected)} onClose={() => setParams({})}>
          <p className="text-sm">{label('plan', selected.subscription || 'none')} · {label('membership', selected.membership || 'none')}</p>
          {selected.pending_downgrade ? (
            <div className="mt-2 space-y-2">
              <p className="text-sm">
                {scheduledPlanChangeSentence(label('plan', selected.pending_downgrade.plan_id), selected.pending_downgrade.effective_at)}
              </p>
              <Button variant="secondary" onClick={() => cancelDowngrade(selected)}>Cancel downgrade</Button>
            </div>
          ) : null}
          <p className="text-sm">{selected.email}</p>
          <p className="mt-4 text-sm">{formatNumber(selected.plan_messages_remaining ?? selected.messages_remaining)} plan messages left</p>
          <p className="text-sm">{formatNumber(selected.purchased_messages_remaining ?? 0)} purchased messages left. Purchased messages do not expire.</p>
          <OwnerPeople users={selected.users || []} tenantId={selected.tenant_id} onNotice={setNotice} />
          <div className="mt-4"><Button variant="tertiary" onClick={() => setPending(selected)}>Hide business</Button></div>
        </Drawer>
      ) : null}
      {pending ? (
        <ConfirmDialog
          title={`Hide ${businessDisplayName(pending)}?`}
          body="This blocks the owner's sign-in and hides the business from lists. No data is deleted. You can unblock the owner later from People."
          confirmLabel="Hide business"
          onClose={() => setPending(null)}
          onConfirm={() => hide(pending)}
        />
      ) : null}
    </div>
  );
}
